import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import SettingsRemoteIcon from "@material-ui/icons/SettingsRemote";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import { Pivot } from "../../dtos/pivot";
import { PivotStartParameters } from "../../dtos/pumpCommandParameter";
import { AjaxResult } from "../../enums/ajaxResult";
import { useApi } from "../apiService";
import { useConfirmDialog } from "../confirmDialogService";
import { useMessage } from "../messageService";
import { makeStyles } from "@material-ui/styles";

interface PivotControlButtonProps {
  pivot: Pivot;
  className?: string;
}

interface PumpOption {
  id: number;
  name: string;
  serialNumber: number;
}

interface ValveOption {
  id: number;
  pumpId: number;
  label: string;
}

type PivotCommandMode = 1 | 2 | 3;
const PIVOT_OVERRIDE_API_KEY = "RJaFYhvbw50vCeY";

const SAMPLE_PUMPS: PumpOption[] = [
  { id: 1, name: "Pump 1", serialNumber: 20001 },
  { id: 2, name: "Pump 2", serialNumber: 20002 },
  { id: 3, name: "Pump 3", serialNumber: 20003 },
  { id: 4, name: "Pump 4", serialNumber: 20004 },
  { id: 5, name: "Pump 5", serialNumber: 20005 }
];

const SAMPLE_VALVES: ValveOption[] = [
  { id: 1, pumpId: 0, label: "Valve 1" },
  { id: 2, pumpId: 0, label: "Valve 2" },
  { id: 3, pumpId: 0, label: "Valve 3" },
  { id: 4, pumpId: 0, label: "Valve 4" },
  { id: 5, pumpId: 0, label: "Valve 5" }
];

const normalizeAngle = (value?: number) => {
  if (value === undefined || value === null) {
    return 0;
  }

  return ((value % 360) + 360) % 360;
};

const getDefaultPivotMode = (pivot: Pivot): PivotCommandMode =>
  pivot.limit_a_action === "reverse" || pivot.limit_b_action === "reverse" ? 2 : 1;

export const PivotControlButton = ({ pivot, className }: PivotControlButtonProps) => {
  const { showConfirmDialog } = useConfirmDialog();
  const { showMessage } = useMessage();
  const { pumpsApi } = useApi();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sessionMinutes, setSessionMinutes] = useState("30");
  const [commandCustomerId, setCommandCustomerId] = useState("1");
  const [commandCustomerNumber, setCommandCustomerNumber] = useState("1");
  const [overrideCustomerCheck, setOverrideCustomerCheck] = useState(true);
  const [commandSerial, setCommandSerial] = useState(pivot.serial.toString());
  const [pivotMode, setPivotMode] = useState<PivotCommandMode>(getDefaultPivotMode(pivot));
  const [dryMoveDirection, setDryMoveDirection] = useState<"CW" | "CCW">(pivot.travel_direction ?? "CW");
  const [dryMoveDegrees, setDryMoveDegrees] = useState("15");
  const [passStartAngle, setPassStartAngle] = useState("0");
  const [passStopAngle, setPassStopAngle] = useState("359");
  const [availablePumps, setAvailablePumps] = useState<PumpOption[]>([]);
  const [selectedPumpIds, setSelectedPumpIds] = useState<number[]>([]);
  const [availableValves, setAvailableValves] = useState<ValveOption[]>([]);
  const [selectedValveIds, setSelectedValveIds] = useState<number[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const classes = useStyles();

  const isFullCircle = (pivot.pivot_type ?? "full") === "full";
  const parsedMinutes = Number(sessionMinutes);
  const isSessionValid = Number.isFinite(parsedMinutes) && parsedMinutes > 0;
  const parsedDryMoveDegrees = Number(dryMoveDegrees);
  const isDryMoveValid = Number.isFinite(parsedDryMoveDegrees) && parsedDryMoveDegrees > 0 && parsedDryMoveDegrees <= 360;
  const parsedPassStart = Number(passStartAngle);
  const parsedPassStop = Number(passStopAngle);
  const arePassAnglesValid =
    Number.isFinite(parsedPassStart) &&
    Number.isFinite(parsedPassStop) &&
    parsedPassStart >= 0 &&
    parsedPassStart <= 359 &&
    parsedPassStop >= 0 &&
    parsedPassStop <= 359;
  const isSessionRunning = pivot.online && pivot.status === "Running";

  const filteredValveOptions = useMemo(
    () => availableValves,
    [availableValves]
  );

  useEffect(() => {
    setCommandCustomerId("1");
    setCommandCustomerNumber("1");
    setCommandSerial(pivot.serial.toString());
    setPivotMode(getDefaultPivotMode(pivot));
    setDryMoveDirection(pivot.travel_direction ?? "CW");
    setPassStartAngle(normalizeAngle(pivot.limit_angle_a).toString());
    setPassStopAngle(normalizeAngle(pivot.limit_angle_b ?? 359).toString());
  }, [pivot]);

  useEffect(() => {
    if (selectedValveIds.length === 0) {
      return;
    }

    const visibleValveIds = new Set(filteredValveOptions.map(valve => valve.id));
    const nextSelectedValveIds = selectedValveIds.filter(valveId => visibleValveIds.has(valveId));

    if (nextSelectedValveIds.length !== selectedValveIds.length) {
      setSelectedValveIds(nextSelectedValveIds);
    }
  }, [filteredValveOptions, selectedValveIds]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    setLoadingEquipment(true);

    const pumpsForPivot = SAMPLE_PUMPS.filter(pump => (pivot.pump_ids ?? []).includes(pump.id));
    const valvesForPivot = SAMPLE_VALVES.filter(valve => (pivot.valve_ids ?? []).includes(valve.id));

    setAvailablePumps(pumpsForPivot);
    setAvailableValves(valvesForPivot);
    setSelectedPumpIds(pumpsForPivot.map(pump => pump.id));
    setSelectedValveIds(valvesForPivot.map(valve => valve.id));
    setLoadingEquipment(false);
  }, [dialogOpen, pivot.pump_ids, pivot.valve_ids]);

  const sendPivotStartCommand = async ({
    mode,
    direction,
    startDegrees,
    stopDegrees
  }: {
    mode: PivotCommandMode;
    direction: "CW" | "CCW";
    startDegrees: number;
    stopDegrees: number;
  }) => {
    const resolvedCustomerId = Number(commandCustomerId);
    const resolvedCustomerNumber = Number(commandCustomerNumber);
    const resolvedSerial = Number(commandSerial);

    if (!Number.isFinite(resolvedCustomerId) || resolvedCustomerId <= 0) {
      showMessage("Enter a customer ID before sending a pivot command.");
      return false;
    }

    if (overrideCustomerCheck && (!Number.isFinite(resolvedCustomerNumber) || resolvedCustomerNumber <= 0)) {
      showMessage("Enter a customer number before sending a pivot command.");
      return false;
    }

    if (!Number.isFinite(resolvedSerial) || resolvedSerial <= 0) {
      showMessage("Enter a command serial before sending a pivot command.");
      return false;
    }

    const pivotStartPayload: PivotStartParameters = {
      customerId: resolvedCustomerId,
      customerNumber: resolvedCustomerNumber,
      apiKey: overrideCustomerCheck ? PIVOT_OVERRIDE_API_KEY : undefined,
      overrideCustomerCheck,
      pumpSerialNumber: resolvedSerial,
      mode,
      speedPercent: Math.max(0, Math.min(100, Math.round(pivot.speed_percent ?? 100))),
      direction: direction === "CCW" ? 1 : 0,
      startDegrees: normalizeAngle(startDegrees),
      stopDegrees: normalizeAngle(stopDegrees)
    };

    console.log("Sending pivotStart command", pivotStartPayload);

    const result = await pumpsApi.pivotStart(pivotStartPayload);

    if (result.result === AjaxResult.Success && result.data) {
      showMessage(
        result.data.success
          ? "Pivot start command received."
          : `Failed to send pivot command: ${result.data.errorMessage}`
      );
      return !!result.data.success;
    }

    showMessage("Failed to send pivot command.");
    return false;
  };

  const handleRunPass = async () => {
    if (isFullCircle) {
      const success = await sendPivotStartCommand({
        mode: pivotMode,
        direction: pivot.travel_direction ?? "CW",
        startDegrees: 0,
        stopDegrees: 359
      });

      if (success) {
        setDialogOpen(false);
      }
      return;
    }

    if (!arePassAnglesValid) {
      showMessage("Enter valid start and stop angles between 0 and 359 degrees.");
      return;
    }

    const success = await sendPivotStartCommand({
      mode: pivotMode,
      direction: pivot.travel_direction ?? "CW",
      startDegrees: parsedPassStart,
      stopDegrees: parsedPassStop
    });

    if (success) {
      setDialogOpen(false);
    }
  };

  const handleTimedSession = async () => {
    if (!isSessionValid) {
      showMessage("Enter a valid session length in minutes.");
      return;
    }

    const confirmed = await showConfirmDialog(
      `Start ${pivot.name} for a ${parsedMinutes}-minute irrigation session?`
    );

    if (!confirmed) {
      return;
    }

    setDialogOpen(false);
    showMessage(`Timed session queued for ${pivot.name}: ${parsedMinutes} minutes.`);
  };

  const handleDryMove = async () => {
    if (!isDryMoveValid) {
      showMessage("Enter a valid dry move amount between 1 and 360 degrees.");
      return;
    }

    const startDegrees = normalizeAngle(pivot.anglePercent);
    const stopDegrees = normalizeAngle(
      dryMoveDirection === "CCW"
        ? startDegrees - parsedDryMoveDegrees
        : startDegrees + parsedDryMoveDegrees
    );
    const success = await sendPivotStartCommand({
      mode: 1,
      direction: dryMoveDirection,
      startDegrees,
      stopDegrees
    });

    if (success) {
      setDialogOpen(false);
    }
  };

  const handleStopSession = async () => {
    const confirmed = await showConfirmDialog(
      `Stop the current session on ${pivot.name}?`
    );

    if (!confirmed) {
      return;
    }

    setDialogOpen(false);
    showMessage(`Stop command queued for ${pivot.name}.`);
  };

  const setValveState = (valveId: number, shouldOpen: boolean) => {
    setSelectedValveIds(currentIds => {
      const isOpen = currentIds.includes(valveId);

      if (shouldOpen && !isOpen) {
        return [...currentIds, valveId];
      }

      if (!shouldOpen && isOpen) {
        return currentIds.filter(id => id !== valveId);
      }

      return currentIds;
    });
  };

  return (
    <>
      <Mui.Button
        variant="contained"
        endIcon={<SettingsRemoteIcon />}
        onClick={() => setDialogOpen(true)}
        className={className || classes.pumpSettingsButton}
      >
        Control
      </Mui.Button>

      <Mui.Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <Mui.DialogTitle>Pivot Control</Mui.DialogTitle>
        <Mui.DialogContent>
          <Mui.Typography variant="body2" color="textSecondary" gutterBottom>
            Choose an action for {pivot.name}.
          </Mui.Typography>

          <Mui.Card variant="outlined" style={{ marginTop: 12, marginBottom: 12 }}>
            <Mui.CardContent>
              <Mui.Typography variant="subtitle2" gutterBottom>
                Run Path
              </Mui.Typography>
              <Mui.Typography variant="body2" color="textSecondary">
                {isFullCircle
                  ? "Run one full circle from the current position."
                  : "Run the pivot across its irrigated path between two angles."}
              </Mui.Typography>
              <Mui.Grid container spacing={2} style={{ marginTop: 4 }}>
                <Mui.Grid item xs={6}>
                  <Mui.TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Customer ID"
                    type="number"
                    value={commandCustomerId}
                    onChange={e => setCommandCustomerId(e.target.value)}
                  />
                </Mui.Grid>
                <Mui.Grid item xs={6}>
                  <Mui.TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Command Serial"
                    type="number"
                    value={commandSerial}
                    onChange={e => setCommandSerial(e.target.value)}
                  />
                </Mui.Grid>
              </Mui.Grid>
              <Mui.FormControlLabel
                style={{ marginTop: 8 }}
                control={
                  <Mui.Checkbox
                    color="primary"
                    checked={overrideCustomerCheck}
                    onChange={e => setOverrideCustomerCheck(e.target.checked)}
                  />
                }
                label="Override customer check"
              />
              {overrideCustomerCheck && (
                <Mui.TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Customer Number"
                  type="number"
                  value={commandCustomerNumber}
                  onChange={e => setCommandCustomerNumber(e.target.value)}
                />
              )}
              <Mui.FormControl
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginTop: 12 }}
              >
                <Mui.InputLabel>End Behavior</Mui.InputLabel>
                <Mui.Select
                  label="End Behavior"
                  value={pivotMode}
                  onChange={e => setPivotMode(e.target.value as PivotCommandMode)}
                >
                  <Mui.MenuItem value={1}>Stop at End</Mui.MenuItem>
                  <Mui.MenuItem value={2}>Reverse at End</Mui.MenuItem>
                  <Mui.MenuItem value={3}>Windshield Wiper</Mui.MenuItem>
                </Mui.Select>
              </Mui.FormControl>
              <Mui.FormControl
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginTop: 12 }}
                disabled={loadingEquipment || availablePumps.length === 0}
              >
                <Mui.InputLabel>Pumps to Start</Mui.InputLabel>
                <Mui.Select
                  multiple
                  value={selectedPumpIds}
                  onChange={e => setSelectedPumpIds(e.target.value as number[])}
                  label="Pumps to Start"
                  renderValue={selected =>
                    availablePumps
                      .filter(pump => (selected as number[]).includes(pump.id))
                      .map(pump => pump.name)
                      .join(", ")
                  }
                >
                  {availablePumps.map(pump => (
                    <Mui.MenuItem key={pump.id} value={pump.id}>
                      <Mui.Checkbox checked={selectedPumpIds.includes(pump.id)} color="primary" />
                      <Mui.ListItemText primary={pump.name} />
                    </Mui.MenuItem>
                  ))}
                </Mui.Select>
                <Mui.FormHelperText>
                  {loadingEquipment
                    ? "Loading available pumps..."
                    : availablePumps.length === 0
                      ? "No pumps available to start."
                      : "Select one or more pumps to start with this run."}
                </Mui.FormHelperText>
              </Mui.FormControl>
              <Mui.FormControl
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginTop: 12 }}
                disabled={loadingEquipment || selectedPumpIds.length === 0 || filteredValveOptions.length === 0}
              >
                <Mui.InputLabel>Valve Positions</Mui.InputLabel>
                <Mui.Select
                  multiple
                  value={selectedValveIds}
                  onChange={() => undefined}
                  label="Valve Positions"
                  renderValue={selected =>
                    filteredValveOptions
                      .map(valve => `${valve.label}: ${(selected as number[]).includes(valve.id) ? "Open" : "Closed"}`)
                      .join(", ")
                  }
                >
                  {filteredValveOptions.map(valve => (
                    <Mui.MenuItem key={valve.id} value={valve.id} style={{ display: "block" }}>
                      <div className={classes.valveStateRow}>
                        <Mui.ListItemText
                          primary={valve.label}
                          secondary={`Will be ${selectedValveIds.includes(valve.id) ? "Open" : "Closed"}`}
                        />
                        <Mui.ButtonGroup size="small" variant="outlined" color="primary">
                          <Mui.Button
                            variant={selectedValveIds.includes(valve.id) ? "contained" : "outlined"}
                            className={selectedValveIds.includes(valve.id) ? classes.valveStateButtonActive : undefined}
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              setValveState(valve.id, true);
                            }}
                          >
                            Open
                          </Mui.Button>
                          <Mui.Button
                            variant={!selectedValveIds.includes(valve.id) ? "contained" : "outlined"}
                            className={!selectedValveIds.includes(valve.id) ? classes.valveStateButtonActive : undefined}
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              setValveState(valve.id, false);
                            }}
                          >
                            Closed
                          </Mui.Button>
                        </Mui.ButtonGroup>
                      </div>
                    </Mui.MenuItem>
                  ))}
                </Mui.Select>
                <Mui.FormHelperText>
                  {loadingEquipment
                    ? "Loading available valves..."
                    : selectedPumpIds.length === 0
                      ? "Select pumps first to see available valves."
                      : filteredValveOptions.length === 0
                        ? "No valves were found for the selected pumps."
                        : "Select valve positions for this run."}
                </Mui.FormHelperText>
              </Mui.FormControl>
              {isFullCircle ? (
                <Mui.Button
                  variant="contained"
                  startIcon={<RotateRightIcon />}
                  onClick={handleRunPass}
                  style={{ marginTop: 12, textTransform: "none" }}
                >
                  Start Full Circle Run
                </Mui.Button>
              ) : (
                <>
                  <Mui.Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Mui.Grid item xs={6}>
                      <Mui.TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Start Angle"
                        type="number"
                        value={passStartAngle}
                        onChange={e => setPassStartAngle(e.target.value)}
                        error={passStartAngle !== "" && !arePassAnglesValid}
                        helperText="0 - 359"
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={6}>
                      <Mui.TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Stop Angle"
                        type="number"
                        value={passStopAngle}
                        onChange={e => setPassStopAngle(e.target.value)}
                        error={passStopAngle !== "" && !arePassAnglesValid}
                        helperText="0 - 359"
                      />
                    </Mui.Grid>
                  </Mui.Grid>
                  <Mui.Button
                    variant="contained"
                    startIcon={<RotateRightIcon />}
                    onClick={handleRunPass}
                    style={{ marginTop: 12, textTransform: "none" }}
                  >
                    Start Run Path
                  </Mui.Button>
                </>
              )}
            </Mui.CardContent>
          </Mui.Card>

          {/* <Mui.Card variant="outlined" style={{ marginBottom: 12 }}>
            <Mui.CardContent>
              <Mui.Typography variant="subtitle2" gutterBottom>
                Start Timed Session
              </Mui.Typography>
              <Mui.TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Session Length (minutes)"
                type="number"
                value={sessionMinutes}
                onChange={e => setSessionMinutes(e.target.value)}
                error={sessionMinutes !== "" && !isSessionValid}
                helperText="Set how long the pivot should run."
              />
              <div className={classes.sessionActions}>
                <Mui.Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleTimedSession}
                  style={{ textTransform: "none" }}
                >
                  Start Session
                </Mui.Button>
                <Mui.Button
                  color="secondary"
                  variant="contained"
                  startIcon={<StopIcon />}
                  onClick={handleStopSession}
                  disabled={!isSessionRunning}
                  className={classes.stopSessionButton}
                  style={{ textTransform: "none" }}
                >
                  Stop Session
                </Mui.Button>
              </div>
            </Mui.CardContent>
          </Mui.Card> */}

          <Mui.Card variant="outlined" style={{ marginBottom: 12 }}>
            <Mui.CardContent>
              <Mui.Typography variant="subtitle2" gutterBottom>
                Dry Move
              </Mui.Typography>
              <Mui.Typography variant="body2" color="textSecondary">
                Move the pivot without pumps running for positioning or maintenance.
              </Mui.Typography>
              <Mui.Grid container spacing={2} style={{ marginTop: 4 }}>
                <Mui.Grid item xs={6}>
                  <Mui.FormControl variant="outlined" size="small" fullWidth>
                    <Mui.InputLabel>Direction</Mui.InputLabel>
                    <Mui.Select
                      label="Direction"
                      value={dryMoveDirection}
                      onChange={e => setDryMoveDirection(e.target.value as "CW" | "CCW")}
                    >
                      <Mui.MenuItem value="CW">Clockwise</Mui.MenuItem>
                      <Mui.MenuItem value="CCW">Counter-Clockwise</Mui.MenuItem>
                    </Mui.Select>
                  </Mui.FormControl>
                </Mui.Grid>
                <Mui.Grid item xs={6}>
                  <Mui.TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Degrees"
                    type="number"
                    value={dryMoveDegrees}
                    onChange={e => setDryMoveDegrees(e.target.value)}
                    error={dryMoveDegrees !== "" && !isDryMoveValid}
                    helperText="1 - 360"
                  />
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Button
                color="default"
                variant="contained"
                startIcon={<AutorenewIcon />}
                onClick={handleDryMove}
                style={{ marginTop: 12, textTransform: "none" }}
              >
                Start Dry Move
              </Mui.Button>
            </Mui.CardContent>
          </Mui.Card>
        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Button onClick={() => setDialogOpen(false)} variant="contained">
            Close
          </Mui.Button>
        </Mui.DialogActions>
      </Mui.Dialog>
    </>
  );
};

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: "95%",
    marginBottom: theme.standardMargin
  },
  tableContainer: {
    maxHeight: "40vh"
  },
  headerSection: {
    display: "flex",
    maxWidth: "900px",
    justifySelf: "center",
    marginBottom: theme.standardMargin,
    "& div": {
      marginLeft: ".5em"
    },
    "& button": {
      marginLeft: ".5em"
    },
    "& a": {
      marginLeft: ".5em"
    }
  },
  serialNumber: {
    color: theme.textColor,
    fontSize: "1em",
    fontWeight: "normal"
  },
  sectionHeader: {
    color: theme.selectedItemColor,
    justifySelf: "start",
    fontSize: "1.25em",
    fontWeight: "bold",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between"
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  },
  informationRoot: {
    width: "100%"
  },
  pumpHistoryButton: {
    backgroundColor: theme.cardHeaderColor,
    textTransform: "none"
  },
  autoControlButton: {
    backgroundColor: theme.mainBackgroundColor,
    color: theme.selectedItemColor,
    textTransform: "none"
  },
  localWeatherButton: {
    backgroundColor: theme.mainBackgroundColor,
    color: theme.selectedItemColor,
    textTransform: "none"
  },
  sessionActions: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1.5),
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1)
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      "& > *:not(:last-child)": {
        marginRight: 0,
        marginBottom: theme.spacing(1)
      }
    }
  },
  valveStateRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%"
  },
  valveStateButtonActive: {
    color: `${theme.whiteColor} !important`,
    backgroundColor: `${theme.selectedItemColor} !important`,
    "&:hover": {
      backgroundColor: `${theme.selectedItemColor} !important`
    }
  },
  stopSessionButton: {
    "&.Mui-disabled": {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[700]
    }
  },
  pumpSettingsButton: {
    backgroundColor: theme.selectedItemColor,
    color: theme.whiteColor,
    textTransform: "none"
  }
}));
